import { useMemo } from 'react';
import { ECARD_DATE_FORMATS } from '@shared/components/EmailsAndEcards/components/Design/Design.constants';
import { EcardType } from '@graphql/generated';
import { withWindow } from '@shared/utils/withWindow';
import { useEcardsTelemetry } from '@apps/ecard/Ecard.telemetry';
import { getDateOnly, isCustomECardType } from '@apps/ecard/Ecard.utils';
import { getDateFormatterForLanguage } from '@localization/formatters/dateFormatter';
import { useEventLocale } from '@shared/core/i18n/LocaleContext';

interface useEcardContentEditorControllerArgs
  extends Readonly<{
    date: string;
    ecardDraftType: EcardType;
    eventHandle: string;
  }> {}

export const useEcardContentEditorController = (args: useEcardContentEditorControllerArgs) => {
  const { date, ecardDraftType, eventHandle } = args;

  const { language } = useEventLocale();

  const isCustomECard = isCustomECardType(ecardDraftType);

  const dateFormats = useMemo(() => {
    if (isCustomECard) {
      return [];
    }

    const formatDate = getDateFormatterForLanguage(language);

    return ECARD_DATE_FORMATS.map(dateFormat => {
      return {
        label: formatDate(getDateOnly(date), dateFormat) ?? '',
        value: dateFormat,
      };
    });
  }, [date, isCustomECard, language]);

  const telemetry = useEcardsTelemetry();
  const onGoToSettingsClick = (linkLabel: 'date' | 'location' | 'url') => {
    telemetry.eCardAdminLinkClick(ecardDraftType, linkLabel);
    withWindow(() => {
      window.open(`${window.location.origin}/${eventHandle}/edit/settings`, '_blank');
    });
  };

  return {
    dateFormats,
    isCustomECard,
    onGoToSettingsClick,
  };
};
